import { Injectable } from "@angular/core";
import { AngularFire } from "angularfire2";
import { UserService } from "./user";
import md5 from "md5";
import Papa from "papaparse";

@Injectable()
export class SettingsService {
  constructor(public af: AngularFire, public userService: UserService) {}

  // Whitelisted Emails
  batchUploadWhiteListedEmails(event: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!event.target.files || !event.target.files.length) {
        return Promise.reject(new Error("No file was chosen."));
      }

      Papa.parse(event.target.files[0], {
        headers: false,
        error: (error: any) => {
          return reject(error);
        },
        complete: (results: any) => {
          if (!results.data || !results.data.length) {
            return reject(new Error("Provided CSV file seems to be malformed"));
          }

          let whitelistedEmails: any[] = [];
          results.data.map(resultRow => {
            if (!resultRow.length) {
              return null;
            }

            let email: string = resultRow[0].toLowerCase().trim();
            if (!email || !this.userService.validateEmail(email)) {
              return null;
            }

            let school: string = resultRow[1].trim();
            if (!school) {
              return null;
            }

            whitelistedEmails.push({
              email: email,
              school: school,
            });
          });

          return resolve(whitelistedEmails);
        },
      });
    }).then((whitelistedEmails: any) => {
      return this.addWhiteListedEmails(whitelistedEmails);
    });
  }

  addWhiteListedEmails(userDetails: any[]): Promise<any> {
    let addWhiteListPromises = [];

    userDetails.map((userDetail: any) => {
      addWhiteListPromises.push(this.addWhiteListedEmail(userDetail));
    });

    return Promise.all(addWhiteListPromises);
  }

  addWhiteListedEmail(userDetails): Promise<any> {
    return new Promise((resolve, reject) => {
      let userEmail = userDetails.email
        ? userDetails.email.toLowerCase().trim()
        : null;
      let userSchool = userDetails.school ? userDetails.school.trim() : null;

      if (!userEmail || !userSchool) {
        return reject(
          new Error("Please enter an email, school name and user type."),
        );
      }
      if (!this.userService.validateEmail(userEmail)) {
        return reject(new Error("Please enter a valid email address."));
      }

      let emailRef = md5(userEmail);

      return this.af.database
        .object(`settings/whitelisted-emails/${emailRef}`)
        .set({
          email: userEmail,
          school: userSchool,
          usertype: UserService.userTypeStudent,
        })
        .then(() => {
          return resolve(userDetails);
        });
    });
  }

  removeWhiteListedEmail(userDetails) {
    return this.removeListItem("/settings/whitelisted-emails", userDetails);
  }

  // Alert message
  updateAlertMessage(alertMessageData: any) {
    return new Promise((resolve, reject) => {
      if (
        !alertMessageData ||
        !alertMessageData.title_en ||
        !alertMessageData.content_en
      ) {
        return reject(
          new Error(
            "Please enter atleast the title and content in english before saving.",
          ),
        );
      }

      let alertMessage = {
        title_en: alertMessageData.title_en,
        title_trm: alertMessageData.title_trm,
        content_en: alertMessageData.content_en,
        content_trm: alertMessageData.content_trm,
      };

      return this.af.database
        .object("/settings/post/alert-message")
        .set(alertMessage)
        .then(() => resolve(alertMessage));
    });
  }

  // Alert phrases
  addAlertPhrase(alertPhrase: string = "new alert phrase") {
    return this.addListItem(
      "/settings/post/alert-phrases",
      alertPhrase.toLowerCase(),
    );
  }

  updateAlertPhrase(alertPhrase: any) {
    alertPhrase.$value = alertPhrase.$value.toLowerCase();

    return this.updateListItem("/settings/post/alert-phrases", alertPhrase);
  }

  removeAlertPhrase(alertPhrase: any) {
    return this.removeListItem("/settings/post/alert-phrases", alertPhrase);
  }

  // Bad words
  addBadWord(badWord: string = "new bad word") {
    return this.addListItem("/settings/post/bad-words", badWord);
  }

  updateBadWord(badWord: any) {
    badWord.$value = badWord.$value.toLowerCase();

    return this.updateListItem("/settings/post/bad-words", badWord);
  }

  removeBadWord(badWord: any) {
    return this.removeListItem("/settings/post/bad-words", badWord);
  }

  // Notification defaults
  updateNotificationDefaults(defaultValues: any) {
    return new Promise((resolve, reject) => {
      if (!defaultValues) {
        return reject(new Error("Please enter some values to set."));
      }

      // Delete any empty values so they're not overwritten
      Object.keys(defaultValues).forEach(prop => {
        if (defaultValues[prop] === "") {
          delete defaultValues[prop];
        }
      });

      return this.af.database
        .object("/settings/notifications/defaults")
        .update(defaultValues)
        .then(() => resolve(defaultValues));
    });
  }

  // List helpers
  addListItem(
    listRef: string,
    defaultValue: string = "New list item",
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      // console.log("Adding", listRef, listRef, defaultValue);

      if (!listRef) {
        return reject(defaultValue);
      }

      return this.af.database
        .list(listRef)
        .push(defaultValue)
        .then(() => {
          let data = {};
          data[listRef] = defaultValue;

          return resolve(data);
        });
    });
  }

  updateListItem(listRef: string, listItem: any): Promise<any> {
    return new Promise((resolve, reject) => {
      // console.log("Updating", listRef, listItem.$key, listItem.$value);

      if (!listRef || !listItem.$key) {
        return reject(
          new Error("No list ref or list item missing $key attribute"),
        );
      }

      return this.af.database
        .object(`${listRef}/${listItem.$key}`)
        .set(listItem.$value)
        .then(() => {
          let data = {};
          data[listRef] = listItem;

          return resolve(data);
        });
    });
  }

  removeListItem(listRef: string, listItem: any): Promise<any> {
    return new Promise((resolve, reject) => {
      // console.log("Removing", listRef, listItem);

      if (!listRef || !listItem.$key) {
        return reject(
          new Error("No list ref or list item missing $key attribute"),
        );
      }

      return this.af.database
        .list(listRef)
        .remove(listItem.$key)
        .then(() => {
          return resolve(true);
        });
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/settings.ts